/**
 * pdfCharts — Helpers d'export PDF extraits de PDFExport.jsx (cap 500).
 *
 * Regroupe :
 *   - la capture des graphiques DOM en canvas (captureElement),
 *   - le rendu d'un graphique + sa légende dans le PDF (addChartToPDF / addChartLegend),
 *   - les sections de contenu « données de référence » (drawThresholdsTable, drawAucSection),
 *   - l'orchestration des 6 graphiques de session (drawSessionCharts).
 *
 * Dépend uniquement des primitives de pdfHelpers.js (module feuille, sans
 * import) → aucun cycle d'import possible. Convention yPos mutable : chaque
 * fonction reçoit puis retourne le yPos courant, à l'identique de pdfHelpers.
 *
 * Le CSS de fond blanc des canvas pendant l'export est construit depuis les
 * selectors de CHART_LAYOUT (injectChartBackgroundOverride) : une seule liste.
 * Le zoom de l'écran est remis à zéro pendant les captures (pdfChartZoom, D240).
 */
import * as Sentry from '@sentry/react';
import i18n from '../i18n';
import { checkPageBreak, drawSectionTitle, drawInfoTable, compositeOnWhite } from './pdfHelpers';
import { hasPcsData } from '../charts/pcsData';
import { PM_THRESHOLDS, CO2_THRESHOLDS, getAucBaselineDay } from '../constants/pmThresholds';
import { formatNumberLocale } from '../utils/formatters';
import { formatPeriodDuration } from '../sessions/period/periodText';
import { hasCo2 } from '../sessions/period/periodAnalysis';
import { resetChartsZoom } from './pdfChartZoom';
import { pdfText } from './pdfPeriod';

// Valeur max recommandée de CO2 telle qu'écrite dans le PDF (« 3 000 »).
const co2ReferenceText = () => formatNumberLocale(CO2_THRESHOLDS.referenceMax, 0);

/**
 * Capture le <canvas> Chart.js d'un conteneur de graphique pour jsPDF.
 * Retourne null si le conteneur est absent OU ne contient pas de canvas peint.
 *
 * ⚠️ Pas de fallback html2canvas ici : depuis le montage PERMANENT des graphiques
 * avancés (SessionCharts), un conteneur sans <canvas> = composant ayant rendu une
 * carte-info « données insuffisantes » (boxplot < 3 jours, AUC < 2 points) ou aucun
 * graphique. Un fallback html2canvas capturerait cette carte (ou le titre/légende DOM)
 * comme une image parasite. On retourne null → les graphiques `conditional` sont
 * proprement omis, et pm10pcs est traité en amont via hasPcsData (section omise).
 */
export async function captureElement(selector, delay = 300) {
  const el = document.querySelector(selector);
  if (!el) return null;
  await new Promise(r => setTimeout(r, delay));
  try {
    const chartCanvas = el.querySelector('canvas');
    if (chartCanvas && chartCanvas.width > 0 && chartCanvas.height > 0) return chartCanvas;
    return null;
  } catch (err) {
    Sentry.captureException(err, { extra: { selector, stage: 'chart_capture' } });
    return null;
  }
}

/**
 * Dessine un encart placeholder (titre + boîte grisée + message centré).
 * Mutualisé : capture nulle (« Graphique indisponible ») et absence de données
 * légitime (message explicite). Convention yPos identique au reste du module.
 */
export function drawChartPlaceholder(pdf, title, message, yPos, margin, contentWidth, pageHeight) {
  yPos = checkPageBreak(pdf, yPos, 30, margin, pageHeight);
  yPos = drawSectionTitle(pdf, pdfText(title), yPos, margin);
  pdf.setFillColor(248, 249, 250);
  pdf.rect(margin, yPos, contentWidth, 20, 'F');
  pdf.setDrawColor(200, 200, 200);
  pdf.rect(margin, yPos, contentWidth, 20, 'S');
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'italic');
  pdf.setTextColor(150, 150, 150);
  pdf.text(pdfText(message), margin + contentWidth / 2, yPos + 11, { align: 'center' });
  return yPos + 25;
}

// Titres, légendes et messages passent par pdfText (glyphes hors WinAnsi).
export function addChartLegend(pdf, lines, yPos, margin, pageWidth) {
  pdf.setFontSize(7.5);
  pdf.setFont('helvetica', 'italic');
  pdf.setTextColor(107, 114, 128);
  lines.forEach((line, i) => {
    pdf.text(pdfText(line), margin, yPos + i * 4.5);
  });
  return yPos + lines.length * 4.5 + 3;
}

/**
 * Ajoute un graphique capturé au PDF
 */
export function addChartToPDF(pdf, canvas, title, yPos, margin, contentWidth, pageHeight) {
  if (!canvas || !canvas.width || !canvas.height) {
    // Placeholder visible — le lecteur du PDF sait qu'un graphique manque
    return drawChartPlaceholder(pdf, title, i18n.t('pdf.charts.unavailable'), yPos, margin, contentWidth, pageHeight);
  }
  try {
    // V1.1-X (sprint V1.2-cleanup) : helper compositeOnWhite mutualisé
    // depuis pdfHelpers.js. JPEG 0.82 préservé à l'identique.
    const imgData = compositeOnWhite(canvas, 0.82);
    if (!imgData || imgData.length < 200) return yPos;
    const imgH = (canvas.height * contentWidth) / canvas.width;
    if (imgH <= 0) return yPos;
    yPos = checkPageBreak(pdf, yPos, imgH + 20, margin, pageHeight);
    yPos = drawSectionTitle(pdf, pdfText(title), yPos, margin);
    pdf.addImage(imgData, 'JPEG', margin, yPos, contentWidth, imgH);
    return yPos + imgH + 8;
  } catch (err) {
    Sentry.captureException(err, { extra: { title, stage: 'chart_to_pdf' } });
    // Dessiner le placeholder en cas d'erreur de conversion
    return drawChartPlaceholder(pdf, title, i18n.t('pdf.charts.unavailable'), yPos, margin, contentWidth, pageHeight);
  }
}

/**
 * Section « Seuils de référence équins » — table statique de seuils indicatifs.
 * @param {{ withCo2?: boolean }} [options] withCo2 : ligne CO₂ « ≤ 3 000 ppm
 *   recommandé » (D253, lot 3), seulement pour une session qui a du CO2 ; une
 *   seule valeur, non alertante, dans la colonne « Bon », les deux autres vides.
 */
export function drawThresholdsTable(pdf, yPos, margin, contentWidth, pageHeight, { withCo2 = false } = {}) {
  yPos = checkPageBreak(pdf, yPos, 55 + (withCo2 ? 6.5 : 0), margin, pageHeight);
  yPos = drawSectionTitle(pdf, i18n.t('pdf.section.thresholds'), yPos, margin);

  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'normal');
  pdf.setTextColor(150, 150, 150);
  pdf.text(i18n.t('pdf.thresholds.note'), margin, yPos);
  yPos += 6;

  // Lignes PM dérivées de PM_THRESHOLDS (warningMax/alertMax) — plus de littéraux dupliqués
  // du JSON (anti-dérive silencieuse, cf. test de parité pdfThresholdsTable). Température /
  // humidité restent littérales (autre source TEMP_/HUMIDITY_THRESHOLDS, hors périmètre dette).
  const pm = PM_THRESHOLDS;
  const pmRow = (name, t) => [name, `<${t.warningMax} µg/m³`, `${t.warningMax}-${t.alertMax} µg/m³`, `>${t.alertMax} µg/m³`];
  // Libellés Température/Humidité : réutilise pdf.stats.row.* (0 doublon). Le connecteur
  // « ou »/« or » via pdf.thresholds.orSep ; bornes numériques (langue-neutres) en dur.
  const orSep = i18n.t('pdf.thresholds.orSep');
  const thresholds = [
    pmRow('PM1',  pm.pm1),
    pmRow('PM2.5', pm.pm25),
    pmRow('PM10', pm.pm10),
    [i18n.t('pdf.stats.row.temperature'), '5-25°C',  '0-5 / 25-27°C',  `<0 ${orSep} >27°C`],
    [i18n.t('pdf.stats.row.humidity'),    '40-70%',  '30-40 / 70-80%', `<30 ${orSep} >80%`],
    ...(withCo2
      ? [[pdfText(i18n.t('pdf.stats.row.co2')), pdfText(i18n.t('pdf.thresholds.co2', { v: co2ReferenceText() })), '', '']]
      : []),
  ];
  const tCols = [0.28, 0.24, 0.24, 0.24];
  const tColX = tCols.map((_, i) => margin + tCols.slice(0, i).reduce((a, b) => a + b, 0) * contentWidth);

  pdf.setFillColor(27, 119, 241);
  pdf.rect(margin, yPos - 4.5, contentWidth, 7, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(8);
  pdf.setFont('helvetica', 'bold');
  [
    i18n.t('pdf.stats.header.parameter'),
    i18n.t('alerts.level.good'),
    i18n.t('alerts.level.watch'),
    i18n.t('alerts.level.high'),
  ].forEach((h, i) => pdf.text(h, tColX[i] + 2, yPos));
  yPos += 7;

  pdf.setFont('helvetica', 'normal');
  thresholds.forEach((row, idx) => {
    if (idx % 2 === 0) { pdf.setFillColor(248, 249, 250); pdf.rect(margin, yPos - 4.5, contentWidth, 6.5, 'F'); }
    pdf.setTextColor(44, 62, 80);  pdf.setFont('helvetica', 'bold'); pdf.text(row[0], tColX[0] + 2, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(16, 185, 129);  pdf.text(row[1], tColX[1] + 2, yPos);
    pdf.setTextColor(245, 158, 11);  if (row[2]) pdf.text(row[2], tColX[2] + 2, yPos);
    pdf.setTextColor(239, 68, 68);   if (row[3]) pdf.text(row[3], tColX[3] + 2, yPos);
    yPos += 6.5;
  });
  yPos += 5;
  return yPos;
}

/**
 * Section « Exposition cumulée (AUC) » — calcul trapézoïdal + table + note.
 * Baseline dérivée du JSON (getAucBaselineDay) et libellé « de référence » (plus « OMS ») :
 * dette [seuils-sourcing-oms-incoherence] traitée — voir DECISIONS D-SEUILS-1.
 * periodDurationMs : période analysée (D240). L'évaluation par jour divise par
 * la durée de la session reçue, ici la période : une note le dit (8 h = 1/3 de jour).
 */
export function drawAucSection(pdf, yPos, session, readings, margin, contentWidth, pageHeight, { periodDurationMs } = {}) {
  // Réserver 65mm : titre + 4 lignes tableau + note
  yPos = checkPageBreak(pdf, yPos, 65, margin, pageHeight);
  yPos = drawSectionTitle(pdf, i18n.t('pdf.section.auc'), yPos, margin);

  // Calcul AUC trapézoïdale depuis les readings ; null si la grandeur n'est
  // jamais mesurée (pas de « 0 dans la norme » fabriqué).
  const isMeasured = (v) => typeof v === 'number' && Number.isFinite(v);
  const calcAUC = (field) => {
    if (!readings || !readings.some(r => isMeasured(r[field]))) return null;
    if (readings.length < 2) return 0;
    let auc = 0;
    for (let i = 1; i < readings.length; i++) {
      const v1 = readings[i-1][field], v2 = readings[i][field];
      if (v1 == null || v2 == null) continue;
      const dtH = (new Date(readings[i].timestamp) - new Date(readings[i-1].timestamp)) / 3_600_000;
      auc += ((v1 + v2) / 2) * dtH;
    }
    return auc;
  };

  const aucPm1  = calcAUC('pm1');
  const aucPm25 = calcAUC('pm2_5');
  const aucPm10 = calcAUC('pm10');
  const aucDays = session.duration_min ? session.duration_min / 1440 : 0;
  const BASELINE = getAucBaselineDay(); // dérivée du JSON : 25 µg/m³ × 24h = 600 µg/m³·h/jour

  const fmtAUC = v => (v < 1000 ? formatNumberLocale(v, 0) : `${formatNumberLocale(v / 1000, 2)}k`);
  const evalAUC = (auc) => {
    // Durée de session inconnue : pas d'évaluation par jour possible.
    if (aucDays <= 0) return { text: i18n.t('common.notProvided'), color: [107,114,128] };
    const perDay = auc / aucDays;
    if (perDay <= BASELINE)       return { text: i18n.t('pdf.auc.eval.within'),   color: [16,185,129] };
    if (perDay <= BASELINE * 1.5) return { text: i18n.t('pdf.auc.eval.high'),     color: [245,158,11] };
    return { text: i18n.t('pdf.auc.eval.veryHigh'), color: [239,68,68] };
  };
  const aucRowValue = (auc) => (auc === null
    ? i18n.t('common.notMeasured')
    : `${fmtAUC(auc)} µg/m3.h  ->  ${evalAUC(auc).text}`);

  yPos = drawInfoTable(pdf, [
    { label: i18n.t('pdf.auc.totalRow', { pollutant: 'PM1' }),   value: aucRowValue(aucPm1) },
    { label: i18n.t('pdf.auc.totalRow', { pollutant: 'PM2.5' }), value: aucRowValue(aucPm25) },
    { label: i18n.t('pdf.auc.totalRow', { pollutant: 'PM10' }),  value: aucRowValue(aucPm10) },
    { label: i18n.t('pdf.auc.baselineLabel'), value: i18n.t('pdf.auc.baselineValue', { value: BASELINE }) },
  ], yPos, margin, contentWidth);

  yPos = checkPageBreak(pdf, yPos, 12, margin, pageHeight);
  pdf.setFontSize(7.5);
  pdf.setFont('helvetica', 'italic');
  pdf.setTextColor(107, 114, 128);
  pdf.text(
    i18n.t('pdf.auc.note'),
    margin, yPos + 3
  );
  yPos += 12;
  if (periodDurationMs != null) {
    pdf.text(i18n.t('pdf.auc.periodNote', {
      duration: formatPeriodDuration(periodDurationMs),
      days: formatNumberLocale(aucDays, 2),
    }), margin, yPos - 5);
    yPos += 4;
  }
  return yPos;
}

/**
 * Layout des 7 graphiques de session (ordre préservé).
 *   selector  : ID DOM capturé (fond blanc forcé par injectChartBackgroundOverride).
 *   delay     : ms d'attente avant capture (laisse le canvas se peindre).
 *   legendBreak : marge checkPageBreak avant la légende (graphiques toujours
 *                 rendus, avec placeholder si capture nulle).
 *   conditional : true = graphique ET légende rendus uniquement si la capture
 *                 réussit, sans checkPageBreak (boxplot/AUC, affichés si ≥ N jours).
 *   co2Guard  : section omise sans CO2 (même juge que l'écran, hasCo2 : la
 *               section #co2-chart-container n'est alors pas dans le DOM).
 *   co2LegendKey : ligne de légende ajoutée quand la session a du CO2 (vue
 *                  combinée : la série CO₂ y est capturée avec les autres).
 */
// i18n : titre/légende stockés en CLÉS (titleKey/legendKey) et résolus au RENDU
// dans drawSessionCharts (i18n.t), JAMAIS ici — sinon la langue serait figée à
// l'import du module. Légendes = tableaux i18next (returnObjects). La légende AUC
// interpole {{baseline}} (getAucBaselineDay).
// noPm10TitleKey / noPm10LegendKey : variantes sans mention du PM10, retenues
// quand la session n'en mesure aucun (capteur externe, D194).
export const CHART_LAYOUT = [
  {
    selector: '#combined-chart-container', delay: 800, conditional: false, legendBreak: 18,
    titleKey: 'pdf.charts.title.combined', legendKey: 'pdf.charts.legend.combined',
    noPm10LegendKey: 'pdf.charts.legendNoPm10.combined',
    co2LegendKey: 'pdf.charts.legend.combinedCo2',
  },
  {
    selector: '#pm-chart-container', delay: 600, conditional: false, legendBreak: 24,
    titleKey: 'pdf.charts.title.pm', legendKey: 'pdf.charts.legend.pm',
    noPm10TitleKey: 'pdf.charts.titleNoPm10.pm', noPm10LegendKey: 'pdf.charts.legendNoPm10.pm',
  },
  {
    selector: '#env-chart-container', delay: 300, conditional: false, legendBreak: 14,
    titleKey: 'pdf.charts.title.env', legendKey: 'pdf.charts.legend.env',
  },
  {
    selector: '#co2-chart-container', delay: 300, conditional: false, legendBreak: 14,
    co2Guard: true, // sans CO2 → section omise (cf. drawChartsInOrder)
    titleKey: 'pdf.charts.title.co2', legendKey: 'pdf.charts.legend.co2',
  },
  {
    selector: '#pm10pcs-chart-container', delay: 300, conditional: false, legendBreak: 14,
    pcsGuard: true, // absence de données pcs → section omise (cf. drawSessionCharts)
    titleKey: 'pdf.charts.title.pm10pcs', legendKey: 'pdf.charts.legend.pm10pcs',
  },
  {
    selector: '#boxplot-chart-container', delay: 500, conditional: true, legendBreak: null,
    titleKey: 'pdf.charts.title.boxplot', legendKey: 'pdf.charts.legend.boxplot',
    noPm10TitleKey: 'pdf.charts.titleNoPm10.boxplot',
  },
  {
    selector: '#auc-chart-container', delay: 500, conditional: true, legendBreak: null,
    titleKey: 'pdf.charts.title.auc', legendKey: 'pdf.charts.legend.auc',
    noPm10TitleKey: 'pdf.charts.titleNoPm10.auc', noPm10LegendKey: 'pdf.charts.legendNoPm10.auc',
  },
];

/**
 * Fond blanc forcé sur les canvas capturés pendant l'export (le thème sombre
 * les peint en transparent). Rend la fonction qui retire la surcharge.
 */
export function injectChartBackgroundOverride() {
  const styleEl = document.createElement('style');
  styleEl.id = 'pdf-export-override';
  styleEl.textContent = `${CHART_LAYOUT.map(ch => `${ch.selector} canvas`).join(',\n')} {
  background: #ffffff !important;
}`;
  document.head.appendChild(styleEl);
  return () => styleEl.remove();
}

/** Session avec au moins une mesure PM10 (une absence = null, jamais 0). */
export function hasPm10Data(readings) {
  return Array.isArray(readings)
    && readings.some(r => typeof r?.pm10 === 'number' && Number.isFinite(r.pm10));
}

/** Clés titre/légende d'un graphique, sans PM10 si la session n'en a pas. */
export function chartTextKeys(chart, withPm10) {
  return {
    titleKey: (!withPm10 && chart.noPm10TitleKey) || chart.titleKey,
    legendKey: (!withPm10 && chart.noPm10LegendKey) || chart.legendKey,
  };
}

/**
 * Capture et insère les graphiques de session dans l'ordre. Préserve le
 * comportement par graphique : graphiques « toujours rendus » (combined/pm/
 * env/co2/pm10pcs) avec placeholder + légende conditionnée à la capture (précédée
 * d'un checkPageBreak, pm10pcs omis sans données pcs, co2 omis sans CO2), vs
 * graphiques « conditionnels » (boxplot/auc) rendus uniquement si la capture
 * réussit, sans checkPageBreak avant la légende.
 */
export async function drawSessionCharts(pdf, yPos, margin, contentWidth, pageWidth, pageHeight, readings) {
  const measured = { withPm10: hasPm10Data(readings), withCo2: hasCo2(null, readings) };
  // Vue complète pendant la capture, avec ou sans période : jamais un zoom
  // laissé à l'écran ; vue de l'écran rétablie ensuite, même en cas d'erreur.
  const restoreZoom = resetChartsZoom(CHART_LAYOUT.map(ch => ch.selector));
  try {
    yPos = await drawChartsInOrder(pdf, yPos, margin, contentWidth, pageWidth, pageHeight, readings, measured);
  } finally {
    restoreZoom();
  }
  return yPos;
}

/** Textes d'un graphique : titre, légende (ligne CO₂ ajoutée quand la session a du CO2). */
export function chartTexts(chart, { withPm10, withCo2 }) {
  const { titleKey, legendKey } = chartTextKeys(chart, withPm10);
  // i18n résolu au rendu (langue UI courante), pas au chargement du module.
  // baseline (légende AUC) et co2Reference (légendes CO2) interpolés, inoffensifs ailleurs.
  const params = { baseline: getAucBaselineDay(), co2Reference: co2ReferenceText() };
  const legend = [...i18n.t(legendKey, { returnObjects: true, ...params })];
  if (withCo2 && chart.co2LegendKey) legend.push(i18n.t(chart.co2LegendKey, params));
  return { title: i18n.t(titleKey), legend };
}

async function drawChartsInOrder(pdf, yPos, margin, contentWidth, pageWidth, pageHeight, readings, measured) {
  for (const ch of CHART_LAYOUT) {
    // pm10pcs : une session sans aucune donnée pcs (capteur externe) n'a pas de
    // comptage à montrer, la section entière est omise plutôt qu'un encadré vide.
    // Même source de vérité que l'écran (hasPcsData), pour ne jamais retomber dans
    // une logique divergente écran/export. Même règle pour le CO2 (hasCo2).
    if (ch.pcsGuard && !hasPcsData(readings)) continue;
    if (ch.co2Guard && !measured.withCo2) continue;
    const { title, legend } = chartTexts(ch, measured);
    const canvas = await captureElement(ch.selector, ch.delay);
    if (ch.conditional) {
      if (canvas) {
        yPos = addChartToPDF(pdf, canvas, title, yPos, margin, contentWidth, pageHeight);
        yPos = addChartLegend(pdf, legend, yPos, margin, pageWidth);
      }
    } else {
      yPos = addChartToPDF(pdf, canvas, title, yPos, margin, contentWidth, pageHeight);
      if (canvas) {
        yPos = checkPageBreak(pdf, yPos, ch.legendBreak, margin, pageHeight);
        yPos = addChartLegend(pdf, legend, yPos, margin, pageWidth);
      }
    }
  }
  return yPos;
}
